import { useState, useEffect, useRef, useCallback } from "react";

/**
 * Custom hook for Google Places autocomplete functionality
 * Handles API calls, debouncing, and session token management
 */
export function usePlacesAutocomplete(value, onChange, onSelect) {
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState(value || "");
  
  const debounceRef = useRef(null);
  const sessionTokenRef = useRef(null);

  // Sync external value
  useEffect(() => {
    setInputValue(value || "");
  }, [value]);

  // Create session token for billing optimization
  useEffect(() => {
    if (window.google?.maps?.places) {
      sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
    }
  }, []);

  // Fetch suggestions - Use backend API as primary (more reliable)
  const fetchSuggestions = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setSuggestions([]);
      return;
    }

    setLoading(true);

    try {
      // Use backend API for autocomplete (works without Google Maps JS SDK)
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/maps/places/autocomplete?input=${encodeURIComponent(query)}`,
        { credentials: 'include' }
      );
      
      if (response.ok) {
        const data = await response.json();
        setLoading(false);
        
        if (data.predictions && data.predictions.length > 0) {
          setSuggestions(data.predictions.map(p => ({
            placeId: p.place_id,
            mainText: p.structured_formatting?.main_text || p.description.split(",")[0],
            secondaryText: p.structured_formatting?.secondary_text || p.description.split(",").slice(1).join(",").trim(),
            description: p.description
          })));
          setShowDropdown(true);
        } else {
          setSuggestions([]);
        }
      } else {
        // Fallback to Google Maps JS SDK if backend fails
        if (window.google?.maps?.places) {
          const service = new window.google.maps.places.AutocompleteService();
          
          service.getPlacePredictions({
            input: query,
            componentRestrictions: { country: "in" },
            sessionToken: sessionTokenRef.current,
            types: ["geocode", "establishment"]
          }, (predictions, status) => {
            setLoading(false);
            if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
              setSuggestions(predictions.map(p => ({
                placeId: p.place_id,
                mainText: p.structured_formatting?.main_text || p.description.split(",")[0],
                secondaryText: p.structured_formatting?.secondary_text || p.description.split(",").slice(1).join(","),
                description: p.description
              })));
              setShowDropdown(true);
            } else {
              setSuggestions([]);
            }
          });
        } else {
          setLoading(false);
          setSuggestions([]);
        }
      }
    } catch (error) {
      console.error("Autocomplete error:", error);
      setLoading(false);
      setSuggestions([]);
    }
  }, []);

  // Handle input change with debounce
  const handleInputChange = useCallback((newValue) => {
    setInputValue(newValue);
    onChange?.(newValue);

    // Clear previous debounce
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // Debounce API calls
    debounceRef.current = setTimeout(() => {
      fetchSuggestions(newValue);
    }, 300);
  }, [onChange, fetchSuggestions]);

  // Handle suggestion selection
  const handleSelect = useCallback(async (suggestion) => {
    setInputValue(suggestion.description);
    onChange?.(suggestion.description);
    setShowDropdown(false);
    setSuggestions([]);

    // Get place details for coordinates
    if (window.google?.maps?.places && suggestion.placeId) {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ placeId: suggestion.placeId }, (results, status) => {
        if (status === "OK" && results[0]) {
          const location = results[0].geometry.location;
          onSelect?.({
            address: suggestion.description,
            lat: location.lat(),
            lng: location.lng(),
            placeId: suggestion.placeId
          });
          // Create new session token after selection
          sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
        } else {
          onSelect?.({ address: suggestion.description });
        }
      });
    } else {
      // Fallback - try backend geocoding
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/maps/geocode?address=${encodeURIComponent(suggestion.description)}`,
          { credentials: 'include' }
        );
        const data = await response.json();
        onSelect?.({
          address: suggestion.description,
          lat: data.lat,
          lng: data.lng
        });
      } catch {
        onSelect?.({ address: suggestion.description });
      }
    }
  }, [onChange, onSelect]);

  const closeDropdown = useCallback(() => {
    setShowDropdown(false);
  }, []);

  const openDropdown = useCallback(() => {
    if (suggestions.length > 0) {
      setShowDropdown(true);
    }
  }, [suggestions.length]);

  return {
    inputValue,
    suggestions,
    showDropdown,
    loading,
    handleInputChange,
    handleSelect,
    closeDropdown,
    openDropdown
  };
}

/**
 * Load Google Maps script with Places library
 */
export function loadGoogleMapsScript() {
  const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  
  return new Promise((resolve, reject) => {
    if (window.google?.maps?.places) {
      resolve(window.google);
      return;
    }

    const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(window.google));
      return;
    }

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.google);
    script.onerror = reject;
    document.head.appendChild(script);
  });
}
